(() => {
  'use strict';
  const escape = value => String(value ?? '').replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
  const roleNames = { client_owner: 'Client Owner', client_manager: 'Client Manager', client_employee: 'Client Contributor' };
  const lockedKeys = new Set(['client.package_changes.approve', 'client.team.manage']);
  const cache = new Map();
  const inflight = new Map();
  const clientProfiles = new Map();
  const statusNames = {
    pending: 'Awaiting Headquarters approval', approved: 'Ready to invite · No access yet',
    declined: 'Declined', cancelled: 'Cancelled', invited: 'Invitation sent',
    accepted: 'Access active', revoked: 'Access revoked'
  };

  async function request(action, payload = {}) {
    const db = window.AASupabaseClient?.getClient?.();
    const cfg = window.AA_SUPABASE_CONFIG;
    if (!db || !cfg?.url || !cfg?.anonKey) throw Error('Headquarters connection is unavailable.');
    const { data, error } = await db.auth.getSession();
    if (error || !data?.session?.access_token) throw Error('Sign in to Headquarters again.');
    const response = await fetch(cfg.url + '/functions/v1/aa-client-team-service', {
      method: 'POST',
      headers: {
        Authorization: 'Bearer ' + data.session.access_token,
        apikey: cfg.anonKey, 'Content-Type': 'application/json'
      },
      body: JSON.stringify({ action, ...payload })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result?.success) throw Error(result.error || 'Could not update client access.');
    return result.data || {};
  }
  function permissionsHtml(data, roleKey, selected) {
    const permissions = (data.permissions || []).filter(p => p.scope === 'client' && !lockedKeys.has(p.permission_key));
    const role = (data.roles || []).find(r => r.role_key === roleKey);
    const defaultKeys = (data.defaults || []).filter(d => d.role_id === role?.id).map(d => d.permission_key);
    const keys = selected || defaultKeys;
    return permissions.map(p => '<label class="aa-client-team__permission"><input type="checkbox" name="permission" value="' +
      escape(p.permission_key) + '" ' + (keys.includes(p.permission_key) ? 'checked' : '') + '><span>' +
      escape(p.description || p.permission_key) + '</span></label>').join('');
  }
  function editorHtml(data, kind, profile = {}) {
    const isOwner = kind === 'owner';
    const roleKey = isOwner ? 'client_owner' : 'client_manager';
    const name = isOwner ? (profile.name || '') : '';
    const email = isOwner ? (profile.email || '') : '';
    return '<form data-aa-team-request-form class="aa-client-team__form">' +
      '<div class="aa-client-team__fields">' +
      '<label>Full name<input name="name" type="text" required maxlength="120" value="' +
        escape(name) + '" placeholder="Full name"></label>' +
      '<label>Email address<input name="email" type="email" required maxlength="254" autocomplete="off" value="' +
        escape(email) + '" placeholder="name@example.com"></label>' +
      (isOwner
        ? '<input type="hidden" name="roleKey" value="client_owner">'
        : '<label>Access role<select name="roleKey" data-aa-team-role>' +
          '<option value="client_manager" selected>Client Manager</option>' +
          '<option value="client_employee">Client Contributor</option>' +
          '</select></label>') +
      '</div>' +
      (isOwner
        ? '<p class="aa-client-team__hint">Prefilled from Client Info. You can edit either field. No login is active yet.</p>' +
          '<input type="hidden" name="note" value="">'
        : '<div data-aa-team-permission-wrapper>' +
          '<p class="aa-client-team__caption">Permissions</p>' +
          '<div class="aa-client-team__permissions" data-aa-team-permissions>' +
            permissionsHtml(data, roleKey) + '</div></div>' +
          '<details class="aa-client-team__optional"><summary>Add a note (optional)</summary>' +
          '<textarea name="note" rows="2" maxlength="1000" aria-label="Optional note for Headquarters"></textarea>' +
          '</details>') +
      '<div class="aa-client-team__form-actions">' +
        '<button type="submit" class="aa-button aa-button--primary">' +
          (isOwner ? 'Save Owner Request' : 'Save Employee Request') + '</button>' +
        '<button type="button" class="aa-button aa-button--secondary" data-aa-team-close-editor>Cancel</button>' +
      '</div></form>';
  }
  function render(host, data, message = '') {
    const members = Array.isArray(data.members) ? data.members : [];
    const requests = Array.isArray(data.requests)
      ? data.requests.filter(item => item.status !== 'cancelled')
      : [];
    const active = members.filter(m => m.active);
    const owner = active.find(m => m.roleKey === 'client_owner');
    const employees = active.filter(m => m.roleKey !== 'client_owner');
    const preparedEmployees = requests.filter(r => r.status === 'approved' &&
      r.requested_role_key !== 'client_owner');
    const ownerRequest = requests.find(r => r.requested_role_key === 'client_owner' &&
      ['pending', 'approved', 'invited'].includes(r.status));
    const profile = clientProfiles.get(host.dataset.aaClientTeamAccess) || {};
    const canPrepareOwner = !owner && !ownerRequest;

    const ownerContent = owner
      ? '<div class="aa-client-team__person"><div><strong>' +
        escape(owner.email) + '</strong><p class="aa-client-team__hint">Active Client Owner</p></div></div>'
      : ownerRequest
        ? '<div class="aa-client-team__person"><div><strong>' +
          escape(ownerRequest.requested_name || profile.name || 'Client Owner') +
          '</strong><p class="aa-client-team__hint">' +
          escape(ownerRequest.requested_email || '') + ' · ' +
          escape(statusNames[ownerRequest.status] || ownerRequest.status) + '</p></div>' +
          (ownerRequest.status === 'approved' && data.isAdmin
            ? '<div class="aa-client-team__actions">' +
              '<button type="button" class="aa-button aa-button--primary" data-aa-team-send-invite="' +
                escape(ownerRequest.id) + '">Send Invitation</button>' +
              '<button type="button" class="aa-button aa-button--secondary" data-aa-team-remove-request data-aa-team-request-id="' +
                escape(ownerRequest.id) + '">Cancel</button></div>'
            : '') +
          '</div>'
        : '<div class="aa-client-team__person"><div><strong>' +
          escape(profile.name || 'Owner not specified') + '</strong><p class="aa-client-team__hint">' +
          escape(profile.email || 'Email not specified') +
          ' · Suggested from Client Info, access not activated</p></div></div>';

    host.innerHTML = '<div class="aa-client-team">' +
      '<header class="aa-client-team__header"><h3>Client Access</h3>' +
      '<p>Who can sign in to this client’s workspace.</p></header>' +
      '<p class="aa-client-team__feedback" role="status" data-aa-team-status>' + escape(message) + '</p>' +
      '<section class="aa-client-team__section">' +
        '<div class="aa-client-team__section-header"><h4>Client Owner</h4>' +
          (canPrepareOwner
            ? '<button type="button" class="aa-button aa-button--secondary" data-aa-team-edit-owner aria-expanded="false">Review Owner</button>'
            : '') +
        '</div>' +
        ownerContent +
        (canPrepareOwner
          ? '<div class="aa-client-team__editor" data-aa-team-owner-editor hidden>' +
            editorHtml(data, 'owner', profile) + '</div>'
          : '') +
      '</section>' +
      '<section class="aa-client-team__section">' +
        '<div class="aa-client-team__section-header"><h4>Employees with Access</h4>' +
          '<button type="button" class="aa-button aa-button--secondary" data-aa-team-add-employee aria-expanded="false">+ Add Employee</button>' +
        '</div>' +
        (employees.length
          ? employees.map(m => '<div class="aa-client-team__person"><strong>' +
              escape(m.email) + '</strong><span>' +
              escape(roleNames[m.roleKey] || m.roleName) + '</span>' +
              (data.isAdmin
                ? '<button type="button" class="aa-button aa-button--secondary" data-aa-team-revoke="' +
                  escape(m.id) + '">Remove Access</button>'
                : '') + '</div>').join('')
          : '<p class="aa-client-team__empty">No employees have access yet.</p>') +
        (preparedEmployees.length
          ? '<div class="aa-client-team__prepared">' +
            '<p class="aa-client-team__caption">Prepared for access · Not invited</p>' +
            preparedEmployees.map(item =>
              '<div class="aa-client-team__person aa-client-team__person--prepared">' +
                '<div><strong>' + escape(item.requested_name) + '</strong>' +
                  '<p class="aa-client-team__hint">' + escape(item.requested_email) +
                  ' · ' + escape(roleNames[item.requested_role_key] || item.requested_role_key) +
                  ' · No access yet</p></div>' +
                (data.isAdmin ? '<div class="aa-client-team__actions">' +
                  '<button type="button" class="aa-button aa-button--primary" data-aa-team-send-invite="' +
                    escape(item.id) + '">Send Invitation</button>' +
                  '<button type="button" class="aa-button aa-button--secondary" data-aa-team-remove-request data-aa-team-request-id="' +
                    escape(item.id) + '">Cancel</button>' +
                  '</div>' : '') +
              '</div>'
            ).join('') +
            '</div>'
          : '') +
        '<div class="aa-client-team__editor" data-aa-team-employee-editor hidden>' +
          editorHtml(data, 'employee') + '</div>' +
      '</section>' +
      '<details class="aa-client-team__disclosure" data-aa-team-history ' +
        (new URLSearchParams(window.location.search).get('aa_review') === '1' ? 'open' : '') +
        '><summary>Request History (' + requests.length + ')</summary>' +
        '<section class="aa-client-team__section">' +
        (requests.length ? requests.map(item =>
          '<article class="aa-client-team__request">' +
          '<div><strong>' + escape(item.requested_name) + '</strong><p>' +
            escape(item.requested_email) + ' · ' +
            escape(roleNames[item.requested_role_key] || item.requested_role_key) + '</p>' +
          '<p class="aa-client-team__request-state">' +
            escape(statusNames[item.status] || item.status) + '</p>' +
          (item.request_note ? '<p>' + escape(item.request_note) + '</p>' : '') + '</div>' +
          (item.status === 'pending' && data.isAdmin
            ? '<div class="aa-client-team__review"><details><summary>Review requested permissions</summary>' +
              '<div class="aa-client-team__permissions">' +
              (item.requested_role_key === 'client_owner'
                ? '<p>Owner access is protected.</p>'
                : permissionsHtml(data,item.requested_role_key,item.requested_permissions || [])) +
              '</div></details><div class="aa-client-team__actions">' +
              '<button type="button" class="aa-button aa-button--secondary" data-aa-team-review="approved" data-aa-team-request-id="' +
              escape(item.id) + '">Approve for Later</button>' +
              '<button type="button" class="aa-button aa-button--secondary" data-aa-team-review="declined" data-aa-team-request-id="' +
              escape(item.id) + '">Decline</button></div></div>'
            : (item.status === 'approved' && data.isAdmin
              ? '<div class="aa-client-team__actions">' +
                '<button type="button" class="aa-button aa-button--primary" data-aa-team-send-invite="' +
                  escape(item.id) + '">Send Invitation</button>' +
                '<button type="button" class="aa-button aa-button--secondary" data-aa-team-remove-request data-aa-team-request-id="' +
                  escape(item.id) + '">Cancel Request</button></div>'
              : '')) +
          '</article>').join('')
        : '<p class="aa-client-team__empty">No requests yet. New approvals appear on the Headquarters dashboard.</p>') +
        '</section></details></div>';
    host.dataset.aaTeamRendered = '1';
    host.dataset.aaTeamClientId = data.client?.id || host.dataset.aaClientTeamAccess;
  }
  function prefetch(clientId) {
    if (!clientId) return Promise.resolve(null);
    if (cache.has(clientId)) return Promise.resolve(cache.get(clientId));
    if (inflight.has(clientId)) return inflight.get(clientId);
    const promise = request('overview', { clientId })
      .then(data => { cache.set(clientId, data); return data; })
      .finally(() => inflight.delete(clientId));
    inflight.set(clientId, promise);
    return promise;
  }
  async function load(host, force = false) {
    const clientId = host.dataset.aaClientTeamAccess;
    if (!clientId || (!force && host.dataset.aaTeamLoading === '1')) return;
    host.dataset.aaTeamLoading = '1';
    const cached = cache.get(clientId);
    if (cached) render(host, cached);
    else host.innerHTML = '<div class="aa-client-team"><h3>Client Access</h3>' +
      '<p class="aa-client-team__loading">Loading access details...</p></div>';
    try {
      const data = force ? await request('overview', {clientId}) : await prefetch(clientId);
      if (force) cache.set(clientId,data);
      if (host.isConnected && host.dataset.aaClientTeamAccess === clientId) render(host, data);
    } catch (error) {
      if (host.isConnected && !cached) host.innerHTML = '<p role="alert" class="aa-client-team__feedback">' +
        escape(error.message) + '</p><button type="button" class="aa-button aa-button--secondary" data-aa-team-retry>Try Again</button>';
    } finally {
      host.dataset.aaTeamLoading = '0';
    }
  }
  function mount(scope = document, client = null) {
    if (client?.id) clientProfiles.set(String(client.id), {
      name: String(client.primary_contact || '').trim(),
      email: String(client.email || '').trim()
    });
    const host = scope.querySelector('[data-aa-client-team-access]');
    if (host && !host.dataset.aaTeamRendered && host.dataset.aaTeamLoading !== '1') load(host);
  }
  document.addEventListener('change', event => {
    const selector = event.target.closest('[data-aa-team-role]');
    if (!selector) return;
    const host = selector.closest('[data-aa-client-team-access]');
    const data = cache.get(host?.dataset.aaClientTeamAccess);
    if (!data) return;
    const wrapper = host.querySelector('[data-aa-team-permission-wrapper]');
    if (wrapper) wrapper.hidden = selector.value === 'client_owner';
    const permissions = host.querySelector('[data-aa-team-permissions]');
    if (permissions) permissions.innerHTML = permissionsHtml(data,selector.value);
  });
  document.addEventListener('submit', async event => {
    const form = event.target.closest('[data-aa-team-request-form]');
    if (!form) return;
    event.preventDefault();
    const host = form.closest('[data-aa-client-team-access]');
    const clientId = host?.dataset.aaClientTeamAccess;
    if (!clientId) return;
    const button = form.querySelector('button[type="submit"]');
    const status = host.querySelector('[data-aa-team-status]');
    button.disabled = true;
    if (status) status.textContent = 'Saving access request...';
    try {
      const payload = {
        clientId, name: form.elements.name.value.trim(), email: form.elements.email.value.trim(),
        roleKey: form.elements.roleKey.value, note: form.elements.note.value.trim(),
        permissionKeys: [...form.querySelectorAll('[name="permission"]:checked')].map(p => p.value)
      };
      const created = await request('create-request',payload);
      await load(host,true);
      const newStatus=host.querySelector('[data-aa-team-status]');
      if (newStatus) newStatus.textContent = created.status === 'approved'
        ? 'Saved. Ready to invite when you choose. No email sent.'
        : 'Request submitted for Headquarters approval. No email sent.';
    } catch (error) {
      if (status) status.textContent=error.message;
      if (button.isConnected) button.disabled=false;
    }
  });
  document.addEventListener('click', event => {
    const owner = event.target.closest('[data-aa-team-edit-owner]');
    const employee = event.target.closest('[data-aa-team-add-employee]');
    const cancel = event.target.closest('[data-aa-team-close-editor]');
    if (!owner && !employee && !cancel) return;
    const host = event.target.closest('[data-aa-client-team-access]');
    if (!host) return;
    const ownerEditor = host.querySelector('[data-aa-team-owner-editor]');
    const employeeEditor = host.querySelector('[data-aa-team-employee-editor]');
    const ownerButton = host.querySelector('[data-aa-team-edit-owner]');
    const employeeButton = host.querySelector('[data-aa-team-add-employee]');
    const chosen = owner ? ownerEditor : employee ? employeeEditor : null;
    if (cancel) {
      if (ownerEditor) ownerEditor.hidden = true;
      if (employeeEditor) employeeEditor.hidden = true;
    } else if (chosen) {
      const open = chosen.hidden;
      if (ownerEditor) ownerEditor.hidden = true;
      if (employeeEditor) employeeEditor.hidden = true;
      chosen.hidden = !open;
      if (open) chosen.querySelector('input[name="name"]')?.focus();
    }
    if (ownerButton) ownerButton.setAttribute('aria-expanded', String(Boolean(ownerEditor && !ownerEditor.hidden)));
    if (employeeButton) employeeButton.setAttribute('aria-expanded', String(Boolean(employeeEditor && !employeeEditor.hidden)));
  });
  document.addEventListener('click', async event => {
    const retry=event.target.closest('[data-aa-team-retry]');
    if (retry) {const host=retry.closest('[data-aa-client-team-access]');if(host)load(host,true);return;}
    const button=event.target.closest('[data-aa-team-review]');
    if (!button) return;
    const host=button.closest('[data-aa-client-team-access]');
    const clientId=host?.dataset.aaClientTeamAccess;
    if(!clientId)return;
    const decision=button.dataset.aaTeamReview;
    const requestId=button.dataset.aaTeamRequestId;
    if (!['approved','declined'].includes(decision))return;
    button.disabled=true;
    const status=host.querySelector('[data-aa-team-status]');
    if(status)status.textContent='Saving decision...';
    try {
      const review = button.closest('.aa-client-team__request');
      const permissionKeys = [...(review?.querySelectorAll('input[name="permission"]:checked') || [])]
        .map(input => input.value);
      await request('review-request',{clientId,requestId,decision,...(decision==='approved'?{permissionKeys}:{})});
      await load(host,true);
      const after=host.querySelector('[data-aa-team-status]');
      if(after)after.textContent=decision==='approved'
        ? 'Request approved. No invitation was sent.'
        : 'Request declined. No invitation was sent.';
    }catch(error){
      if(status)status.textContent=error.message;
      if(button.isConnected)button.disabled=false;
    }
  });
  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-aa-team-remove-request]');
    if (!button) return;
    const host = button.closest('[data-aa-client-team-access]');
    const clientId = host?.dataset.aaClientTeamAccess;
    const requestId = button.dataset.aaTeamRequestId;
    if (!clientId || !requestId) return;
    // Explicit second click before permanent deletion; no modal.
    if (button.dataset.aaTeamConfirmed !== 'yes') {
      button.dataset.aaTeamConfirmed = 'yes';
      button.textContent = 'Confirm Remove';
      button.focus();
      return;
    }
    button.disabled = true;
    const status = host.querySelector('[data-aa-team-status]');
    if (status) status.textContent = 'Removing uninvited request...';
    try {
      await request('remove-uninvited-request', {clientId,requestId});
      await load(host,true);
      const feedback = host.querySelector('[data-aa-team-status]');
      if(feedback) feedback.textContent = 'Request removed. No invitation was sent.';
    } catch (error) {
      if (status) status.textContent = error.message;
      if (button.isConnected) button.disabled=false;
    }
  });
  document.addEventListener('click', async event => {
    const invite = event.target.closest('[data-aa-team-send-invite]');
    const revoke = event.target.closest('[data-aa-team-revoke]');
    const button = invite || revoke;
    if (!button) return;
    const host = button.closest('[data-aa-client-team-access]');
    const clientId = host?.dataset.aaClientTeamAccess;
    if (!clientId) return;
    if (button.dataset.aaTeamConfirmed !== 'yes') {
      button.dataset.aaTeamConfirmed = 'yes';
      button.textContent = invite ? 'Confirm Send Invitation' : 'Confirm Remove Access';
      button.focus();
      return;
    }
    button.disabled = true;
    const status = host.querySelector('[data-aa-team-status]');
    if (status) status.textContent = invite ? 'Sending invitation...' : 'Removing employee access...';
    try {
      if (invite) {
        await request('send-invitation',{clientId,requestId:invite.dataset.aaTeamSendInvite});
      } else {
        await request('deactivate-member',{clientId,membershipId:revoke.dataset.aaTeamRevoke});
      }
      await load(host,true);
      const feedback = host.querySelector('[data-aa-team-status]');
      if (feedback) feedback.textContent=invite?'Invitation sent. Waiting for acceptance.':'Employee access removed.';
    } catch(error) {
      if(status) status.textContent=error.message;
      if(button.isConnected) button.disabled=false;
    }
  });
  window.AAClientTeamAccess = { mount, prefetch };
})();
